import { Box, DownIcon, IconButton, Stack, Typography, UpIcon, useTheme } from "@carrotfertility/carotene-core"
import React, { useId, useState } from "react"
import { useIntl } from "react-intl"
import { AppointmentsAvatar } from "./AppointmentsAvatar"
import { getAppointmentTypeLabel } from "./appointmentTypeLabels"
import { formatPastAppointmentDate, formatProviderName } from "./formatting"
import { perViewport, perViewportTypography } from "./responsive"
import type { PastAppointment } from "./types"

/**
 * How many visits the design leaves on screen before the chevron. Frame 5 draws two, and the reveal
 * is a *partial* one — the first two rows never move, unlike an accordion over the whole list.
 *
 * Flat across both viewports, and frame 9 is the evidence rather than a judgement call: its Past
 * section draws two `.mob-row`s, then `<div id="pastMoreMob" hidden>` wrapping the other two, plus
 * the same `.sec-toggle` chevron. So mobile keeps the reveal and only the column count changes. The
 * secondhand reading of frame 9 as "four rows, all visible" — which would have left the chevron
 * pointing at nothing — does not survive the markup. See CONAPP-271.
 */
const ALWAYS_VISIBLE_ROWS = 2

/**
 * Newest first, by plain string comparison: `PastAppointment.date` is a zero-padded `YYYY-MM-DD`, so
 * lexical order already is chronological order and there is nothing to parse and no locale to get
 * wrong. Sorting here rather than trusting the caller means the real API can return any order.
 */
const byNewestFirst = (a: PastAppointment, b: PastAppointment): number => {
  const left = a?.date ?? ""
  const right = b?.date ?? ""
  if (left === right) {
    return 0
  }
  return left < right ? 1 : -1
}

/**
 * One past visit. A plain `Box`, not the `Paper` the Up next card uses: `S4-design.html:124`
 * (`.setc .row,.row{background:transparent}`) overrides the `background: var(--bg-paper)` on `.row`,
 * and both frames that draw this section carry `setc`. Same token vocabulary as the card, honestly
 * different primitive — a `Paper` here would be a surface asked not to be a surface.
 *
 * Geometry is `.row-in` verbatim: `padding: --space-lg`, `gap: --space-md`, and `align-items:
 * flex-start` from the `.gcols .row-in` override. The radius only shows if a hover ground ever lands
 * on the row.
 *
 * Identity only — no action, and so nothing to gate on `status`. If a "Book again" affordance ever
 * comes back it must not appear on a `"canceled"` visit (see CONAPP-263).
 */
function PastAppointmentRow({ appointment }: { appointment: PastAppointment }): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()
  const { provider, appointmentType, date } = appointment

  return (
    <Box
      sx={{
        display: "flex",
        /*
          Three metrics, all of which frame 9 restates rather than inherits. `.gcols .row-in` is a
          24px box with a 16px gap on `align-items: flex-start`, because at desktop the row is a CARD
          shape inside a two-column grid. `.mob-row` + `.mob-lead` is `padding: --space-md 0`, a 12px
          gap and centred: in one column there is no card to inset, so the inline padding goes to zero
          and the rows sit flush with the section heading and the CTAs above them.
        */
        alignItems: perViewport("center", "flex-start"),
        gap: perViewport(theme.spacing(theme.tokens.spacing.sm), theme.spacing(theme.tokens.spacing.md)),
        paddingBlock: perViewport(theme.spacing(theme.tokens.spacing.md), theme.spacing(theme.tokens.spacing.lg)),
        paddingInline: perViewport(theme.spacing(theme.tokens.spacing.none), theme.spacing(theme.tokens.spacing.lg)),
        borderRadius: theme.borderRadius(theme.tokens.borderRadius.md)
      }}
    >
      {/* No `size`: the component default IS the appointment-row size, 54px desktop / 48px mobile. */}
      <AppointmentsAvatar name={provider?.name} photoUrl={provider?.photoUrl} />
      <Box sx={{ minInlineSize: 0 }}>
        {/* `.nm` → `.mob-nm`, h5 → h6. `variant` stays so the heading LEVEL does not move. */}
        <Typography variant="h5" sx={{ typography: perViewportTypography("h6", "h5") }}>
          {formatProviderName(provider)}
        </Typography>
        {/* `body1` is already `text.secondary` in the Carotene theme, matching the design's `.mt`. */}
        <Typography sx={{ typography: perViewportTypography("body2", "body1") }}>
          {getAppointmentTypeLabel(appointmentType)}
        </Typography>
        {/*
          `.d2` is `display: block`, so the date sits on its own line under the type. Guarded because
          `formatDate(undefined)` would quietly render *today*, which is a worse answer than no date
          at all.
        */}
        {date && (
          <Typography sx={{ typography: perViewportTypography("body2", "body1") }}>
            {formatPastAppointmentDate(intl, date)}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

/**
 * `.gcols` — two equal columns inside the main column, `minmax(0,1fr)` so a long provider name
 * shrinks its cell instead of widening the grid. Frame 9 stacks the same rows into one column.
 *
 * The only column declaration in the section, so both grid instances change together and the visible
 * rows can never get out of step with the revealed ones.
 */
function PastAppointmentGrid({
  appointments,
  id,
  hidden
}: {
  appointments: PastAppointment[]
  id?: string
  hidden?: boolean
}): JSX.Element {
  const theme = useTheme()

  return (
    <Box
      id={id}
      hidden={hidden}
      sx={{
        // `display` stays flat on purpose. It is what the `[hidden]` override below has to outrank,
        // and a breakpoint object here would emit `display: grid` inside media queries that the
        // single `&[hidden]` rule could no longer reliably beat.
        display: "grid",
        gridTemplateColumns: perViewport("minmax(0, 1fr)", "repeat(2, minmax(0, 1fr))"),
        gap: theme.spacing(theme.tokens.spacing.md),
        // An author `display: grid` outranks the UA's `[hidden] { display: none }`, so the attribute
        // needs this to actually hide anything — the same override the design writes as
        // `.gcols[hidden]{display:none}`.
        "&[hidden]": { display: "none" }
      }}
    >
      {appointments.map((appointment) => (
        <PastAppointmentRow key={appointment.id} appointment={appointment} />
      ))}
    </Box>
  )
}

/**
 * The Past section from frames 5 and 8: a two-column grid of transparent rows, the first two always
 * on screen and the rest behind an icon-only chevron.
 *
 * The overflow grid stays mounted under the `hidden` attribute rather than being conditionally
 * rendered. That is the design's own mechanism (`.gcols[hidden]`), it removes the rows from the
 * accessibility tree exactly as an unmount would, and it keeps `aria-controls` pointing at an
 * element that exists in both states. Carotene exports no `Collapse`, so either way there is no
 * animation — which is also what the design does.
 */
export function PastAppointments({
  appointments
}: {
  /**
   * Nullable on purpose — the section renders nothing for a missing list just as it does for an empty
   * one, which `PastAppointments.test.tsx` covers both ways. Explicit here because this package is
   * `strict`, where the host it moved from was not.
   */
  appointments: PastAppointment[] | null | undefined
}): JSX.Element | null {
  const theme = useTheme()
  const [expanded, setExpanded] = useState(false)
  const revealId = `past-appointments-earlier-${useId()}`

  // No frame in the design draws an empty Past section. Kept here rather than in `page.tsx` so the
  // section stays self-contained; CONAPP-270 owns lifting it if the composition reads better that way.
  if (appointments == null || appointments.length === 0) {
    return null
  }

  const ordered = [...appointments].sort(byNewestFirst)
  const visibleAppointments = ordered.slice(0, ALWAYS_VISIBLE_ROWS)
  const earlierAppointments = ordered.slice(ALWAYS_VISIBLE_ROWS)

  return (
    <Stack sx={{ flexDirection: "column" }} spacing={theme.spacing(theme.tokens.spacing.md)}>
      {/* `.sec-row` — heading left, chevron pushed to the far edge of the main column. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: theme.spacing(theme.tokens.spacing.md)
        }}
      >
        <Typography variant="h3">Past</Typography>
        {/* Nothing to reveal, no toggle: with two visits or fewer the whole list is already on screen. */}
        {earlierAppointments.length > 0 && (
          <IconButton
            // `color="primary"` is a type error on IconButton; `inherit` also matches the design's
            // `.sec-toggle { color: var(--text-primary) }` rather than tinting the chevron.
            color="inherit"
            size="small"
            aria-expanded={expanded}
            aria-controls={revealId}
            aria-label={expanded ? "Collapse earlier visits" : "Show earlier visits"}
            onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
          >
            {expanded ? <UpIcon /> : <DownIcon />}
          </IconButton>
        )}
      </Box>
      <PastAppointmentGrid appointments={visibleAppointments} />
      {earlierAppointments.length > 0 && (
        <PastAppointmentGrid id={revealId} appointments={earlierAppointments} hidden={!expanded} />
      )}
    </Stack>
  )
}
